import React, { useCallback, useEffect, useRef } from "react";
import _ from "lodash";
import {
  Button,
  Row,
} from "antd";
import {
  CellRenderers,
} from "utils/tableUtils";
import { TableContainerEvolvedWithoutRouter } from "components/TableContainerEvolved";
import Vocabulary from 'components/Vocabulary';
import TrimText from 'views/Findings/TrimText'
import { CWE } from './CWE';
import FindingAPI from "restAPI/FindingAPI";
import EnvironmentName from "views/GlobalSettings/EnvironmentName";
import { SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES } from "./Constants";
import FindingDetailsPopup from "./FindingDetails/FindingDetailsPopup";
import useModalRenderer from "hooks/useModalRenderer";
import { FindingModalCommonConfig } from "./FindingDetails/common";

const SimilarFindingsTable = ({ sourceFinding, scope, updateType, tagCondition, severity, category, findingUpdateTaskType, similarFindingsToNotUpdate, ...restProps }) => {
  const tableRef = useRef();
  const modalRenderer = useModalRenderer();

  useEffect(() => {
    tableRef.current?.refresh && tableRef.current.refresh();
  }, [scope, updateType, tagCondition,])

  const getSimilarFindingsList = useCallback(async (param) => {
    try {
      if (_.isUndefined(sourceFinding)) {
        return;
      }

      const {
        page,
        size,
      } = param || {};
      const findingsList = await FindingAPI.similarFindings.getSimilarFindings({
        scope,
        updateType,
        contains: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.contains,
        equals: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.equals,
        findingId: sourceFinding,
        category,
        severity,
        findingUpdateTaskType,
        page,
        size
      }).promise;

      return findingsList;
    } catch (err) {
      console.error(err);
    }
  }, [sourceFinding, scope, updateType, tagCondition, category, severity, findingUpdateTaskType]);

  const showFindingDetail = (e) => {
    const findingId = e.currentTarget.dataset.id;
    modalRenderer.show({
      content: <FindingDetailsPopup
        findingId={findingId}
      />,
      ...FindingModalCommonConfig,
    })
  };

  const getColumns = () => {
    const columns = [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        render: (text) => (
          <Button
            type="link"
            size="small"
            data-id={text}
            onClick={showFindingDetail}
          >
            {text}
          </Button>
        ),
        width: 120,
      },
      {
        title: <Vocabulary name="severity" />,
        dataIndex: "severity",
        key: "severity",
        render: CellRenderers.CUSTOMIZED_SEVERITY,
        width: 128,
        optional: true,
        default: true
      },
      {
        title: <Vocabulary name="product" />,
        dataIndex: "product",
        key: "product",
        render: CellRenderers.NAME_ID_MAP,
        width: 110,
        optional: true,
        default: true
      },
      {
        title: <Vocabulary name="subProduct" />,
        dataIndex: "subProduct",
        key: "subProduct",
        render: CellRenderers.NAME_ID_MAP,
        width: 110,
        optional: true,
        default: true
      },

      {
        title: "Environment",
        dataIndex: "environmentName",
        key: "environmentName",
        render: (text, record) => <EnvironmentName name={record.environment} />,
        width: 120,
        optional: true,
        default: true
      },
      {
        title: "Tags",
        dataIndex: "findingTags",
        key: "findingTags",
        width: 120,
        render: CellRenderers.TAGS,
        optional: true,
        default: true,
        nullFilterValue: null,
        isMultipleValueSupported: true
      },
      {
        title: "Summary",
        dataIndex: "title",
        key: "title",
        width: 300,
        render: (title) => {
          return <TrimText content={title || ''} length={100} />
        },
        optional: true,
        default: true
      },
      {
        title: "CWE",
        dataIndex: "cwesStrings",
        key: "cwesStrings",
        render: CWE,
        width: 100,
      },
      {
        title: "Source Tool",
        dataIndex: "source",
        key: "source",
        render: (value) => _.isObject(value) ? value.name : value,
        width: 120,
      },
      {
        title: "Developer",
        dataIndex: "developer",
        key: "developer",
        render: CellRenderers.VALUE_OR_NA,
        width: 110,
        optional: true,
        default: true
      }
    ];

    return columns;
  };

  const rowSelection = {
    similarFindingsToNotUpdate: similarFindingsToNotUpdate.current,
    columnWidth: 30,
    onChange: (ids) => similarFindingsToNotUpdate.current = ids,
  };

  return (
    <>
    <TableContainerEvolvedWithoutRouter
      tableRef={ref => tableRef.current = ref}
      disableURLUpdate
      rowSelection={rowSelection}
      showColumnOptions={false}
      key={'similar-findings-embeded-table'}
      api={getSimilarFindingsList}
      columns={getColumns()}
      enableScroll
      locale={
        { emptyText: <>No Finding(s)</> }
      }
      showSizeChanger
      extras={<Row className="required-mark text-bold">Select findings which should not be updated.</Row>}
      {...restProps}
    />
    {modalRenderer.context}
    </>
  );
};

export default SimilarFindingsTable;